import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, At, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { auth } from "@/lib/api";
import { BlonixWordmark } from "@/components/BlonixLogo";

const HERO = "https://images.unsplash.com/photo-1713981910533-49bd42cafd60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzgxNDY0NDN8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@blonix.io");
  const [password, setPassword] = useState("Blonix@2025");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await auth.login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left: form */}
      <div className="flex flex-col p-8 lg:p-14">
        <div>
          <BlonixWordmark className="h-6 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Card Platform</div>
        </div>

        <div className="flex-1 flex items-center max-w-md w-full mx-auto">
          <div className="w-full">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
              Internal Console — Authorized Personnel
            </div>
            <h1 className="font-display text-4xl tracking-tight font-medium text-zinc-950">
              Sign in to Blonix.
            </h1>
            <p className="mt-3 text-sm text-zinc-500 max-w-sm">
              Operate merchant onboarding, card issuance, and treasury controls
              across the Blonix issuing fabric.
            </p>

            <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Email</label>
                <div className="relative">
                  <At size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="email"
                    required
                    data-testid="login-email-input"
                    className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    data-testid="login-password-input"
                    className="w-full h-10 pl-9 pr-9 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  >
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-10 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
              >
                {loading ? "Authenticating…" : (<>Continue <ArrowRight size={14} /></>)}
              </button>

              <div className="pt-4 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">Default Bootstrap Credential</div>
                <div className="text-xs font-mono text-zinc-700">admin@blonix.io · Blonix@2025</div>
              </div>
            </form>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">
          © {new Date().getFullYear()} Blonix Technologies Limited
        </div>
      </div>

      {/* Right: hero */}
      <div className="hidden lg:block relative bg-zinc-950 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/80 via-zinc-950/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-3">Issuer Network</div>
          <div className="font-display text-3xl leading-tight tracking-tight max-w-md">
            One platform. Every merchant. Total control over the issuance stack.
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-sm">
            <Stat n="100%" l="Issuer abstracted" />
            <Stat n="< 80ms" l="proxy latency p95" />
            <Stat n="SOC2-ready" l="audit pipeline" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-xl font-medium tabular-nums">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-white/60 mt-0.5">{l}</div>
    </div>
  );
}
